import { buildApiUrl, ENDPOINTS, API_VERSION_V2, API_BASE_URL } from '../config/api.config';
import { ReportData, Report } from '../types/reportTypes';
import { FilterState } from '../types/filterTypes';
import { filterStateToQueryParams } from '../utils/filterUtils';

// Cache configuration
const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_SIZE_LIMIT = 50; // Maximum number of cached responses

// Create a simple cache for API responses
interface CacheEntry {
  data: any;
  timestamp: number;
}

// Cache object to store responses
const apiCache: Record<string, CacheEntry> = {};

// Pagination parameters interface
export interface PaginationParams {
  page: number;
  pageSize: number;
  totalItems?: number;
  totalPages?: number;
}

// Paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Service for handling paginated report data requests
 * This service extends the report service with server-side pagination support
 */
class PaginatedReportService {
  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  };

  /**
   * Adds a response to the cache
   * @param cacheKey Unique key for the cached response
   * @param data Response data to cache
   */
  private cacheResponse(cacheKey: string, data: any): void {
    // Add to cache with current timestamp
    apiCache[cacheKey] = {
      data,
      timestamp: Date.now()
    };

    // Check if cache size exceeds limit
    const cacheKeys = Object.keys(apiCache);
    if (cacheKeys.length > CACHE_SIZE_LIMIT) {
      // Find oldest entry
      let oldestKey = cacheKeys[0];
      let oldestTime = apiCache[oldestKey].timestamp;

      cacheKeys.forEach(key => {
        if (apiCache[key].timestamp < oldestTime) {
          oldestKey = key;
          oldestTime = apiCache[key].timestamp;
        }
      });

      // Remove oldest entry
      delete apiCache[oldestKey];
    }
  }

  /**
   * Checks if a cached response exists and is valid
   * @param cacheKey Unique key for the cached response
   * @returns Cached data or null if not available
   */
  private getCachedResponse(cacheKey: string): any | null {
    const cachedEntry = apiCache[cacheKey];

    // No cached data
    if (!cachedEntry) {
      return null;
    }

    // Check if cache has expired
    const isExpired = (Date.now() - cachedEntry.timestamp) > CACHE_EXPIRATION;
    if (isExpired) {
      // Remove expired cache entry
      delete apiCache[cacheKey];
      return null;
    }

    return cachedEntry.data;
  }

  /**
   * Clears a specific cache entry or all cache if no key provided
   * @param cacheKey Optional key to clear specific entry
   */
  public clearCache(cacheKey?: string): void {
    if (cacheKey) {
      delete apiCache[cacheKey];
    } else {
      // Clear all cache
      Object.keys(apiCache).forEach(key => delete apiCache[key]);
    }
  }

  /**
   * Handle API request errors with improved logging
   */
  private async handleResponse(response: Response, endpoint: string) {
    if (!response.ok) {
      try {
        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error(`API error (${endpoint}): ${response.status} - ${errorData.message || response.statusText}`);
          throw new Error(errorData.message || `API error: ${response.status}`);
        } else {
          // Handle non-JSON error responses
          const errorText = await response.text();
          console.error(`API error (${endpoint}): ${response.status} - ${response.statusText}`, errorText);
          throw new Error(`API error: ${response.status} - ${response.statusText}`);
        }
      } catch (e) {
        console.error(`API error (${endpoint}): ${response.status} - ${response.statusText}`);
        throw new Error(`API error: ${response.status}`);
      }
    }
    return response;
  }

  /**
   * Handle request with automatic retry for network and authentication errors
   * @param url The URL to fetch
   * @param options Request options
   * @param endpoint Endpoint name for logging
   * @param retryCount Current retry count (internal use)
   * @param maxRetries Maximum number of retries
   * @param retryDelay Base delay between retries in ms (increases with retries)
   * @returns Response object
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    endpoint: string,
    retryCount = 0,
    maxRetries = 3,
    retryDelay = 1000
  ): Promise<Response> {
    try {
      // Create cache key for GET requests (don't cache POST, PUT, DELETE)
      const isGetRequest = !options.method || options.method === 'GET';
      const cacheKey = isGetRequest ? `${url}_${JSON.stringify(options.headers)}` : '';

      // Check cache for GET requests
      if (isGetRequest && retryCount === 0) {
        const cachedResponse = this.getCachedResponse(cacheKey);
        if (cachedResponse) {
          console.log(`Using cached response for ${endpoint}`);
          return {
            ok: true,
            status: 200,
            json: () => Promise.resolve(cachedResponse),
            headers: new Headers({
              'Content-Type': 'application/json',
              'X-Cache': 'HIT'
            })
          } as Response;
        }
      }

      const response = await fetch(url, options);

      // Handle specific error cases
      if (response.status === 401 && endpoint !== 'auth/refresh') {
        console.log('Token expired, attempting to refresh...');

        try {
          // Use the getAuthUrl helper from apiService
          const baseUrl = API_BASE_URL.includes('/api')
            ? API_BASE_URL.replace('/api', '')
            : API_BASE_URL;

          const cleanBase = baseUrl.endsWith('/')
            ? baseUrl.slice(0, -1)
            : baseUrl;

          const refreshUrl = `${cleanBase}/api/auth/refresh`;
          console.log('Token refresh URL:', refreshUrl);
          const refreshResponse = await fetch(refreshUrl, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ refreshToken: localStorage.getItem('refreshToken') }),
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();

            // Update tokens in localStorage
            localStorage.setItem('authToken', refreshData.accessToken || refreshData.access_token);

            if (refreshData.refreshToken) {
              localStorage.setItem('refreshToken', refreshData.refreshToken);
            }

            console.log('Token refreshed successfully, retrying original request');

            // Retry the original request with the new token
            options.headers = {
              ...options.headers,
              Authorization: `Bearer ${refreshData.accessToken || refreshData.access_token}`,
            };

            // Reset retry count after token refresh
            return this.fetchWithRetry(url, options, endpoint, 0, maxRetries, retryDelay);
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          // Continue with retry logic
        }
      }

      // If we have a successful response
      if (response.ok) {
        // For GET requests, cache the response
        if (isGetRequest) {
          try {
            // Clone the response to avoid "already read" errors
            const clonedResponse = response.clone();
            const data = await clonedResponse.json();
            this.cacheResponse(cacheKey, data);
          } catch (cacheError) {
            console.error('Error caching response:', cacheError);
            // Continue without caching
          }
        }
        return response;
      }

      // For retryable errors (network issues, server errors)
      const isRetryable =
        response.status >= 500 || // Server errors
        response.status === 429 || // Rate limiting
        response.status === 408;   // Request timeout

      // If we can retry and have retries left
      if (isRetryable && retryCount < maxRetries) {
        // Calculate exponential backoff delay
        const delay = retryDelay * Math.pow(2, retryCount);
        console.log(`Retrying request to ${endpoint} (${retryCount + 1}/${maxRetries}) after ${delay}ms...`);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));

        // Retry with incremented count
        return this.fetchWithRetry(url, options, endpoint, retryCount + 1, maxRetries, retryDelay);
      }

      return response;
    } catch (error) {
      // Handle network errors with retries
      if (retryCount < maxRetries) {
        // Calculate exponential backoff delay
        const delay = retryDelay * Math.pow(2, retryCount);
        console.error(`Network error calling ${endpoint}, retrying (${retryCount + 1}/${maxRetries}) after ${delay}ms...`, error);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));

        // Retry with incremented count
        return this.fetchWithRetry(url, options, endpoint, retryCount + 1, maxRetries, retryDelay);
      }

      console.error(`Network error calling ${endpoint} after ${maxRetries} retries:`, error);
      throw error;
    }
  }

  /**
   * Get report data with monthly period (simplified API for interactive reports)
   * @returns Monthly report data
   */
  async getMonthlyReport(): Promise<any> {
    // Simplified endpoint for monthly reports
    const endpoint = `${API_VERSION_V2}/reports/generate`;
    console.log(`Fetching monthly report`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          period: 'monthly',
          filters: {}
        })
      },
      endpoint
    );

    const responseData = await this.handleResponse(response, 'getMonthlyReport').then(r => r.json());

    // Extract the actual data from the Laravel API response format
    const data = responseData.data;

    console.log(`Retrieved monthly report data with ${data.period_data.length} data points`);

    return data;
  }

  /**
   * Get paginated reports with filtering
   * @param filterState Filter state object
   * @param pagination Pagination parameters
   * @returns Paginated list of reports
   */
  async getPaginatedReports(
    filterState: FilterState,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<Report>> {
    // Convert filter state to query parameters
    const queryParams = filterStateToQueryParams(filterState);

    // Add pagination parameters
    queryParams.page = pagination.page.toString();
    queryParams.pageSize = pagination.pageSize.toString();

    // Build query string from parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        // Handle arrays (like sources, campaigns, etc.)
        if (Array.isArray(value)) {
          return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');

    // Adjust endpoint for Laravel API - use v2 prefix for reports
    const endpoint = `${API_VERSION_V2}/reports?${queryString}`;
    console.log(`Fetching paginated reports: page ${pagination.page}, size ${pagination.pageSize}`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      endpoint
    );

    const data = await this.handleResponse(response, 'getPaginatedReports').then(r => r.json());

    console.log(`Retrieved ${data.data.length} of ${data.pagination.totalItems} reports (page ${data.pagination.page} of ${data.pagination.totalPages})`);

    // If the API doesn't return in the expected format, transform the response
    if (!data.pagination) {
      return {
        data: data,
        pagination: {
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalItems: pagination.totalItems || data.length,
          totalPages: pagination.totalPages || Math.ceil((pagination.totalItems || data.length) / pagination.pageSize)
        }
      };
    }

    return data;
  }

  /**
   * Get paginated report detail with large dataset handling
   * @param reportId Report ID
   * @param filterState Filter state object
   * @param pagination Pagination parameters for large datasets
   * @returns Paginated report data
   */
  async getPaginatedReportDetail(
    reportId: string,
    filterState: FilterState,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<any>> {
    // Convert filter state to query parameters
    const queryParams = filterStateToQueryParams(filterState);
    
    // Add pagination parameters
    queryParams.page = pagination.page.toString();
    queryParams.pageSize = pagination.pageSize.toString();
    
    // Build query string from parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        // Handle arrays (like sources, campaigns, etc.)
        if (Array.isArray(value)) {
          return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');
    
    // Adjust endpoint for Laravel API using v2 prefix
    const endpoint = `${API_VERSION_V2}/reports/${reportId}/paginated?${queryString}`;
    console.log(`Fetching paginated report detail: ${reportId}, page ${pagination.page}, size ${pagination.pageSize}`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      endpoint
    );
    
    const data = await this.handleResponse(response, 'getPaginatedReportDetail').then(r => r.json());
    
    // Check if data is already in the expected format
    if (data.pagination) {
      console.log(`Retrieved report data: page ${data.pagination.page} of ${data.pagination.totalPages}`);
      return data;
    }
    
    // If the API doesn't return in the expected format, transform it
    console.log(`Retrieved report data (transformed format)`);
    return {
      data: data.jobData || data.data || [],
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems || (data.jobData?.length || data.data?.length || 0),
        totalPages: pagination.totalPages || Math.ceil((pagination.totalItems || (data.jobData?.length || data.data?.length || 0)) / pagination.pageSize)
      }
    };
  }

  /**
   * Get ad spend by source with pagination (for large data sets)
   * @param reportId Report ID
   * @param filterState Filter state object
   * @param pagination Pagination parameters
   * @returns Paginated ad spend by source data
   */
  async getPaginatedAdSpendBySource(
    reportId: string,
    filterState: FilterState,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<any>> {
    // Convert filter state to query parameters
    const queryParams = filterStateToQueryParams(filterState);
    
    // Add pagination parameters
    queryParams.page = pagination.page.toString();
    queryParams.pageSize = pagination.pageSize.toString();
    
    // Build query string from parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        // Handle arrays (like sources, campaigns, etc.)
        if (Array.isArray(value)) {
          return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');
    
    // Adjust endpoint for Laravel API using v2 prefix
    const endpoint = `${API_VERSION_V2}/reports/${reportId}/adspend-sources?${queryString}`;
    console.log(`Fetching paginated ad spend data: ${reportId}, page ${pagination.page}`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      endpoint
    );
    
    const data = await this.handleResponse(response, 'getPaginatedAdSpendBySource').then(r => r.json());
    
    if (data.pagination) {
      console.log(`Retrieved ad spend data: page ${data.pagination.page} of ${data.pagination.totalPages}`);
      return data;
    }
    
    // If not returned in paginated format, transform it
    console.log(`Retrieved ad spend data (transformed format)`);
    return {
      data: data.adSpendBySource || data,
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems || (data.adSpendBySource?.length || data.length || 0),
        totalPages: pagination.totalPages || Math.ceil((pagination.totalItems || (data.adSpendBySource?.length || data.length || 0)) / pagination.pageSize)
      }
    };
  }

  /**
   * Get sales by source with pagination
   * @param reportId Report ID
   * @param filterState Filter state object
   * @param pagination Pagination parameters
   * @returns Paginated sales by source data
   */
  async getPaginatedSalesBySource(
    reportId: string,
    filterState: FilterState,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<any>> {
    // Convert filter state to query parameters
    const queryParams = filterStateToQueryParams(filterState);
    
    // Add pagination parameters
    queryParams.page = pagination.page.toString();
    queryParams.pageSize = pagination.pageSize.toString();
    
    // Build query string from parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        // Handle arrays (like sources, campaigns, etc.)
        if (Array.isArray(value)) {
          return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');
    
    // Adjust endpoint for Laravel API using v2 prefix
    const endpoint = `${API_VERSION_V2}/reports/${reportId}/sales-sources?${queryString}`;
    console.log(`Fetching paginated sales data: ${reportId}, page ${pagination.page}`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      endpoint
    );
    
    const data = await this.handleResponse(response, 'getPaginatedSalesBySource').then(r => r.json());
    
    if (data.pagination) {
      console.log(`Retrieved sales data: page ${data.pagination.page} of ${data.pagination.totalPages}`);
      return data;
    }
    
    // If not returned in paginated format, transform it
    console.log(`Retrieved sales data (transformed format)`);
    return {
      data: data.salesBySource || data,
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems || (data.salesBySource?.length || data.length || 0),
        totalPages: pagination.totalPages || Math.ceil((pagination.totalItems || (data.salesBySource?.length || data.length || 0)) / pagination.pageSize)
      }
    };
  }

  /**
   * Get job data with pagination (for large reports)
   * @param reportId Report ID
   * @param filterState Filter state object
   * @param pagination Pagination parameters
   * @returns Paginated job data
   */
  async getPaginatedJobData(
    reportId: string,
    filterState: FilterState,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<any>> {
    // Convert filter state to query parameters
    const queryParams = filterStateToQueryParams(filterState);
    
    // Add pagination parameters
    queryParams.page = pagination.page.toString();
    queryParams.pageSize = pagination.pageSize.toString();
    
    // Build query string from parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        // Handle arrays (like sources, campaigns, etc.)
        if (Array.isArray(value)) {
          return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');
    
    // Adjust endpoint for Laravel API using v2 prefix
    const endpoint = `${API_VERSION_V2}/reports/${reportId}/jobs?${queryString}`;
    console.log(`Fetching paginated job data: ${reportId}, page ${pagination.page}`);

    const response = await this.fetchWithRetry(
      buildApiUrl(endpoint),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      endpoint
    );
    
    const data = await this.handleResponse(response, 'getPaginatedJobData').then(r => r.json());
    
    if (data.pagination) {
      console.log(`Retrieved job data: page ${data.pagination.page} of ${data.pagination.totalPages}`);
      return data;
    }
    
    // If not returned in paginated format, transform it
    console.log(`Retrieved job data (transformed format)`);
    return {
      data: data.jobData || data,
      pagination: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalItems || (data.jobData?.length || data.length || 0),
        totalPages: pagination.totalPages || Math.ceil((pagination.totalItems || (data.jobData?.length || data.length || 0)) / pagination.pageSize)
      }
    };
  }

  /**
   * Check if the current user has permission to access the requested report
   * @param reportId The ID of the report to check
   * @returns True if the user has permission, false otherwise
   */
  async checkReportPermission(reportId: string): Promise<boolean> {
    try {
      // Use v2 prefix for permission check
      const endpoint = `${API_VERSION_V2}/reports/${reportId}/access`;

      const response = await this.fetchWithRetry(
        buildApiUrl(endpoint),
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
        endpoint
      );

      // If response is ok, user has permission
      if (response.ok) {
        return true;
      }

      // Otherwise, if 403 Forbidden, user doesn't have permission
      if (response.status === 403) {
        console.warn(`User doesn't have permission to access report ${reportId}`);
        return false;
      }

      // For other errors, handle with usual error handling
      await this.handleResponse(response, 'checkReportPermission');
      return false;
    } catch (error) {
      console.error('Error checking report permission:', error);
      return false;
    }
  }

  /**
   * Generate a sample paginated response for development and testing
   * @param itemCount Total number of items
   * @param pagination Pagination parameters
   * @returns Sample paginated data
   */
  getSamplePaginatedData<T>(
    items: T[],
    pagination: PaginationParams
  ): PaginatedResponse<T> {
    const { page, pageSize } = pagination;
    const totalItems = items.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    
    // Calculate start and end indices for the current page
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    
    // Slice the data for the current page
    const pageData = items.slice(startIndex, endIndex);
    
    return {
      data: pageData,
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages
      }
    };
  }
}

// Create and export singleton instance
const paginatedReportService = new PaginatedReportService();
export default paginatedReportService;